import dayjs from "dayjs";
import { DemandeAbsenceType, DemandesAValiderType } from "../../api/DemandeAbsence/types";
import { DemandeAValider, EtatDemande, NbTerminees } from "./EspaceValideur.types";

export const estAbsence = (demande: DemandeAValider): demande is DemandeAbsenceType => 'dateDebut' in demande;

// Les identifiants des demandes d'absence et de CET peuvent se recouper
export const cleDemande = (demande: DemandeAValider) => `${estAbsence(demande) ? 'abs' : 'cet'}-${demande.identifiant}`;

// Trie les demandes de la plus récemment créée à la plus ancienne
export const trierParCreation = <T extends DemandeAValider>(demandes: T[]) => (
    [...demandes].sort((a, b) => dayjs(b.dateDemande).valueOf() - dayjs(a.dateDemande).valueOf())
);

// Aplatit la réponse d'une route "a-valider" en conservant l'état de chaque demande renvoyé par le serveur
export const repartirDemandes = <T extends DemandeAValider>(reponse?: Partial<DemandesAValiderType<T>>) => {
    const etats: Record<string, EtatDemande> = {};
    const groupes: [T[] | undefined, EtatDemande][] = [
        [reponse?.aValider, 'attente'],
        [reponse?.acceptees, 'valide'],
        [reponse?.refusees, 'refuse'],
    ];
    const demandes = groupes.flatMap(([liste, etat]) => (liste ?? []).map((demande) => {
        etats[cleDemande(demande)] = etat;
        return demande;
    }));
    // Nombre de demandes dont la chaîne de validation est terminée, chargées à part
    const totaux: NbTerminees = {
        valide: reponse?.totaux?.validees ?? 0,
        refuse: reponse?.totaux?.refusees ?? 0,
    };
    return { demandes, etats, totaux };
}

// La chaîne de validation est terminée dès que la demande a été acceptée (1) ou refusée (-1) par tous les niveaux
export const estTerminee = (demande: DemandeAValider) => demande.statut !== 0;

export const toNumber = (valeur?: string | number | null) => Number(String(valeur ?? '').replace(',', '.')) || 0;

// Les durées sont affichées avec 2 décimales au maximum
export const formatNombre = (valeur: number) => valeur.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

export const formatJours = (valeur: number) => `${formatNombre(valeur)} j`;

export const formatHeures = (totalMinutes: number) => {
    const signe = totalMinutes < 0 ? '− ' : '';
    const absolu = Math.abs(Math.round(totalMinutes));
    const heures = Math.floor(absolu / 60);
    const minutes = absolu % 60;
    if(!heures){
        return `${signe}${minutes} min`;
    }
    return `${signe}${heures} h${minutes ? ` ${String(minutes).padStart(2, '0')}` : ''}`;
}

// Soldes horaires renvoyés par l'API au format "H:MM" (éventuellement négatifs)
export const heuresTexteEnMinutes = (valeur?: string) => {
    const texte = String(valeur ?? '').trim();
    const negatif = texte.startsWith('-');
    const [heures, minutes] = texte.replace('-', '').split(':');
    const total = Number(heures || 0) * 60 + Number(minutes || 0);
    return negatif ? -total : total;
}

// Les durées en heures des dotations sont au format H.MM (ex : 2.30 = 2 h 30)
export const heuresDotationEnMinutes = (valeur?: string | number | null) => {
    const duree = toNumber(valeur);
    const heures = Math.trunc(duree);
    return heures * 60 + Math.round((duree - heures) * 100);
}

// Une demande CET en jours n'a que son champ jour renseigné, une demande en heures que son champ heure
export const joursDemandeCet = (demande: DemandeAValider, heuresParJour: number) => {
    if(estAbsence(demande)){
        return 0;
    }
    const jours = toNumber(demande.jour);
    if(jours){
        return jours;
    }
    return heuresDotationEnMinutes(demande.heure) / 60 / heuresParJour;
}

export const texteDuree = (demande: DemandeAValider, heuresParJour: number) => {
    if(estAbsence(demande)){
        return demande.duree?.texte || '';
    }
    return formatJours(joursDemandeCet(demande, heuresParJour));
}

export const textePeriode = (demande: DemandeAValider) => {
    if(!estAbsence(demande)){
        return `Créée le ${dayjs(demande.dateDemande).format('DD/MM/YYYY')}`;
    }
    const debut = dayjs(demande.dateDebut).format('DD/MM/YYYY');
    const fin = dayjs(demande.dateFin).format('DD/MM/YYYY');
    return debut === fin ? `Le ${debut}` : `Du ${debut} au ${fin}`;
}
